import { confirmar } from '@/components/ui/confirm-dialog';
import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Copy,
  FileText,
  Calendar,
  Mail,
  User,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { contractService } from '../services/contract.service';
import type { Contract, ContractStatus } from '../types/contract.types';

// ── Labels ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  EXPIRED: 'Vencido',
  CANCELLED: 'Cancelado',
};

const STATUS_VARIANTS: Record<ContractStatus, 'secondary' | 'success' | 'destructive' | 'outline'> =
  {
    DRAFT: 'secondary',
    ACTIVE: 'success',
    EXPIRED: 'destructive',
    CANCELLED: 'outline',
  };

const TYPE_LABELS: Record<string, string> = {
  CONTRACT: 'Contrato',
  TERMS: 'Términos',
  PRIVACY: 'Privacidad',
  AMENDMENT: 'Adenda',
  OTHER: 'Otro',
};

// ── Simple Markdown → HTML ──────────────────────────────────────

function renderMarkdown(md: string): string {
  return md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr class="my-4 border-white/10" />')
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br />')
    .replace(/^(.+)$/, '<p class="mb-2">$1</p>');
}

// ── Page ────────────────────────────────────────────────────────

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusChanging, setStatusChanging] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    contractService
      .getById(id)
      .then(setContract)
      .catch(() =>
        toast({ title: 'Error', description: 'Contrato no encontrado', variant: 'destructive' })
      )
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleStatusChange = async (newStatus: string) => {
    if (!contract) return;
    try {
      setStatusChanging(true);
      const updated = await contractService.update(contract.id, {
        status: newStatus as ContractStatus,
      });
      setContract(updated);
      toast({ title: `Estado cambiado a ${STATUS_LABELS[newStatus as ContractStatus]}` });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado',
        variant: 'destructive',
      });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleDelete = async () => {
    if (!contract) return;
    if (
      !(await confirmar({
        title: '¿Eliminar este contrato?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await contractService.remove(contract.id);
      toast({ title: 'Contrato eliminado' });
      navigate('/contracts');
    } catch {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleClone = async () => {
    if (!contract) return;
    try {
      const cloned = await contractService.clone(contract.id);
      toast({ title: 'Contrato clonado' });
      navigate(`/contracts/${cloned.id}/edit`);
    } catch {
      toast({ title: 'Error', description: 'No se pudo clonar', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (!contract) {
    return (
      <AppLayout>
        <div className="p-6 text-center text-muted-foreground">
          <p>Contrato no encontrado</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/contracts')}>
            Volver al listado
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="mb-3">
                <EtiquetaDePagina modulo="cotiza">Cotiza · Gestión</EtiquetaDePagina>
              </div>
              <h1 className={CLASE_TITULO_DE_PAGINA}>
                {contract.name}
                {contract.isTemplate && (
                  <Badge variant="outline" className="text-xs">
                    Plantilla
                  </Badge>
                )}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={STATUS_VARIANTS[contract.status]}>
                  {STATUS_LABELS[contract.status]}
                </Badge>
                <span className="text-sm text-muted-foreground">{TYPE_LABELS[contract.type]}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClone}>
              <Copy className="h-4 w-4 mr-1" /> Clonar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/contracts/${contract.id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-1" /> Editar
            </Button>
            {contract.status === 'DRAFT' && (
              <Button variant="destructive" size="sm" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-1" /> Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Meta info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-white/10">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Estado:</span>
                <Select
                  value={contract.status}
                  onValueChange={handleStatusChange}
                  disabled={statusChanging}
                >
                  <SelectTrigger className="w-[140px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Borrador</SelectItem>
                    <SelectItem value="ACTIVE">Activo</SelectItem>
                    <SelectItem value="EXPIRED">Vencido</SelectItem>
                    <SelectItem value="CANCELLED">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {contract.createdBy && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Creado por:</span>
                  <span>
                    {contract.createdBy.firstName} {contract.createdBy.lastName}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Creado:</span>
                <span>{new Date(contract.createdAt).toLocaleDateString('es-BO')}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10">
            <CardContent className="pt-4 space-y-3">
              {contract.clientName && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Cliente:</span>
                  <span>{contract.clientName}</span>
                </div>
              )}
              {contract.clientEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email:</span>
                  <span>{contract.clientEmail}</span>
                </div>
              )}
              {contract.quote && (
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Cotización:</span>
                  <Link to={`/quotes/${contract.quote.id}`} className="text-info hover:underline">
                    {contract.quote.code}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-white/10">
            <CardContent className="pt-4 space-y-3">
              {contract.validFrom && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Válido desde:</span>
                  <span>{new Date(contract.validFrom).toLocaleDateString('es-BO')}</span>
                </div>
              )}
              {contract.validTo && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Válido hasta:</span>
                  <span>{new Date(contract.validTo).toLocaleDateString('es-BO')}</span>
                </div>
              )}
              {!contract.validFrom && !contract.validTo && (
                <p className="text-sm text-muted-foreground">Sin fechas de vigencia definidas</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <Card className="border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Contenido del Contrato</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-invert max-w-none text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(contract.content) }}
            />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
